// src/components/sections/Menu.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import Card from "../ui/Card";
import ImageLoader from "../ui/ImageLoader";
import { useTranslation } from "react-i18next";
import api from "../../admin/api";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const FALLBACK = "/assets/menu/placeholder.jpg";

// Custom arrows positioned inside slider
function NextArrow({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="absolute top-1/2 right-2 -translate-y-1/2 z-50 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
    >
      <FiChevronRight size={24} />
    </button>
  );
}

function PrevArrow({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="absolute top-1/2 left-2 -translate-y-1/2 z-50 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
    >
      <FiChevronLeft size={24} />
    </button>
  );
}

export default function Menu() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("de") ? "de" : "en";
  const { ref, isInView } = useScrollAnimation();
  const sliderRef = useRef(null);

  const [cats, setCats] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const priceFmt = useMemo(
    () =>
      new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", {
        style: "currency",
        currency: "EUR",
      }),
    [lang]
  );

  useEffect(() => {
    let mounted = true;
    api
      .get("/api/public/menu", { params: { lang } })
      .then((res) => {
        if (!mounted) return;
        const data = res.data?.categories || [];
        setCats(data);
        if (!data.find((c) => c._id === activeId)) setActiveId(data[0]?._id || "");
      })
      .catch((e) => setErr(e?.response?.data?.error || "Failed to load menu"))
      .finally(() => setLoading(false));

    return () => { mounted = false; };
  }, [lang]);

  const activeCat = cats.find((c) => c._id === activeId);
  const dishes = activeCat?.dishes || [];

  // Keep the slider at selected category
  const handleCategoryClick = (catId, index) => {
    setActiveId(catId);
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index); // keep clicked category visible
    }
  };

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow: 2.5, 
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    swipeToSlide: true,
    variableWidth: true, // let long names show fully
  };

  return (
    <section
      id="menu"
      ref={ref}
      className="scroll-mt-[72px] -mt-[72px] pb-16 sm:pb-20 md:pb-24 bg-gray-900 text-white"
    >
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        >
          <h2 className="font-display text-4xl">{lang === "de" ? "Speisekarte" : "Menu"}</h2>
          <div className="w-16 h-1 bg-yellow-500 mx-auto mt-3" />
        </motion.div>

        {/* Mobile category slider - horizontal line */}
        <div className="md:hidden mb-6 relative">
          {cats.length > 0 && (
            <Slider ref={sliderRef} {...sliderSettings}>
              {cats.map((cat, index) => {
                const active = activeId === cat._id;
                return (
                  <button
                    key={cat._id}
                    onClick={() => handleCategoryClick(cat._id, index)}
                    className={`px-3 py-2 rounded-md text-sm font-medium border whitespace-nowrap ${
                      active
                        ? "bg-yellow-600 border-yellow-600 text-black font-semibold"
                        : "bg-gray-800 border-gray-700 text-gray-200"
                    }`}
                  >
                    {cat.name}
                  </button>
                );
              })}
            </Slider>
          )}
        </div>

        {/* Desktop categories */}
        <div className="hidden md:flex flex-wrap justify-center mb-8 gap-2">
          {cats.map((cat) => {
            const active = activeId === cat._id;
            return (
              <button
                key={cat._id}
                onClick={() => setActiveId(cat._id)}
                className={`px-4 py-2 rounded-md text-sm md:text-base transition border ${
                  active
                    ? "bg-yellow-600 border-yellow-600 text-black font-semibold"
                    : "bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700"
                }`}
              >
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* Dishes Grid (all screens, no slider) */}
        {!loading && !err && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {dishes.length === 0 ? (
              <div className="col-span-full text-center text-gray-200">
                {lang === "de" ? "Keine Gerichte in dieser Kategorie." : "No dishes in this category."}
              </div>
            ) : (
              dishes.map((item) => (
                <Card key={item._id} className="overflow-hidden bg-gray-800 border border-gray-700">
                  <div className="flex items-stretch">
                    <div className="w-44 sm:w-56 h-40 sm:h-44 shrink-0">
                      <ImageLoader
                        src={item.imageUrl || FALLBACK}
                        alt={item.title}
                        className="w-full h-full object-cover rounded-l-xl"
                        onError={(e) => { e.currentTarget.src = FALLBACK; }}
                      />
                    </div>
                    <div className="flex-1 p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-lg font-semibold text-gray-600">{item.title}</h3>
                        <span className="text-yellow-400 font-semibold whitespace-nowrap">{priceFmt.format((item.priceCents || 0) / 100)}</span>
                      </div>
                      {item.description && <p className="text-sm text-gray-600 mt-1 leading-6">{item.description}</p>}
                      <div className="mt-2 text-xs text-gray-600 flex gap-3">
                        {item.isVeg && <span>• {lang==="de"?"Vegetarisch":"Veg"}</span>}
                        {item.isSpicy && <span>• {lang==="de"?"Scharf":"Spicy"}</span>}
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </section>
  );
}
